import { type ReactNode, useState } from 'react';
import { IconChevronRight } from '@rossum/ui/icons/tabler';
import {
  Box,
  ButtonBase,
  Collapse,
  Stack,
  SvgIcon,
  type SxProps,
  type Theme,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

type CollapsibleSectionProps = {
  label: ReactNode;
  children: ReactNode;
  startIcon?: ReactNode;
  defaultExpanded?: boolean;
  sx?: SxProps<Theme>;
  'data-cy'?: string;
};

export const CollapsibleSection = ({
  label,
  children,
  startIcon,
  defaultExpanded = false,
  sx,
  'data-cy': dataCy,
}: CollapsibleSectionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box data-cy={dataCy} sx={sx}>
      <ButtonBase
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        sx={{
          justifyContent: 'flex-start',
          borderRadius: borderRadius('s'),
          px: 0.5,
          py: 0.25,
          ml: -0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <SvgIcon
            sx={{
              fontSize: 14,
              color: 'text.secondary',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <IconChevronRight />
          </SvgIcon>
          {startIcon}
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Stack>
      </ButtonBase>
      <Collapse in={expanded} timeout={200}>
        {children}
      </Collapse>
    </Box>
  );
};
